/**
 * Utility functions for medication data processing
 */

import { sentenceCase } from 'sentence-case';

/**
 * Check if a medication record is an external RX
 * Handles both v1 (priority.name) and v2 (currentImportanceLevel) data structures
 * @param {Object} record - Medication record
 * @returns {boolean}
 */
export const isExternalRxRecord = record => {
  // Check taxonomyName first (works for both structures)
  if (record.taxonomyName?.toLowerCase() === 'other') {
    return true;
  }

  // Check v2 structure (currentImportanceLevel as string)
  if (
    typeof record.currentImportanceLevel === 'string' &&
    record.currentImportanceLevel.toLowerCase().includes('external')
  ) {
    return true;
  }

  // Check v1 structure (priority object with name field)
  if (
    record.priority &&
    typeof record.priority.name === 'string' &&
    record.priority.name.toLowerCase().includes('external')
  ) {
    return true;
  }

  return false;
};

/**
 * Get display text for medication importance level
 * Handles special case for ACTIVE_EXTERNAL_RX
 * @param {string} level - Importance level
 * @returns {string} - Display text
 */
export const getImportanceLevelDisplayText = level => {
  if (level?.toUpperCase() === 'ACTIVE_EXTERNAL_RX') {
    return 'External Active';
  }
  if (level?.toUpperCase() === 'INACTIVE_EXTERNAL_RX') {
    return 'External Inactive';
  }
  return sentenceCase(level || '');
};

/**
 * Flatten external RX records by grouping them by pharmacyNotes (unique ID)
 * Records with the same pharmacyNotes are the same external RX (main + update history)
 * @param {Array} records - Array of medication records from API
 * @returns {Array} - Flattened array of medication records
 */
export const flattenExternalRxRecords = records => {
  if (!records?.length) return [];

  return records.flatMap(record => {
    const isExternalRx = isExternalRxRecord(record);

    if (isExternalRx && record.history?.length > 0) {
      const mainRecordId = record.tagMetaData?.rxDrugInfo?.pharmacyNotes;

      // Old data: no pharmacyNotes on main record - flatten main + all history as separate cards
      if (!mainRecordId) {
        const flattened = [{ ...record, history: [] }];
        record.history.forEach(historyItem => {
          flattened.push({
            ...historyItem,
            taxonomyName: record.taxonomyName,
            domainTypeId: record.domainTypeId,
            domainTypeName: record.domainTypeName,
            // Handle both v1 (priority) and v2 (currentImportanceLevel) structures
            currentImportanceLevel: historyItem.importanceLevel,
            priority: historyItem.importanceLevel || historyItem.priority,
          });
        });
        return flattened;
      }

      // New data: group by pharmacyNotes
      const groupedByExternalRxId = {
        [mainRecordId]: { main: { ...record, history: [] }, historyItems: [] },
      };

      record.history.forEach(historyItem => {
        const historyItemId = historyItem.tagMetaData?.rxDrugInfo?.pharmacyNotes;

        if (historyItemId === mainRecordId) {
          // Same external RX - add to history
          groupedByExternalRxId[mainRecordId].historyItems.push(historyItem);
        } else if (historyItemId) {
          // Different external RX with ID
          if (!groupedByExternalRxId[historyItemId]) {
            groupedByExternalRxId[historyItemId] = {
              main: {
                ...historyItem,
                taxonomyName: record.taxonomyName,
                domainTypeId: record.domainTypeId,
                domainTypeName: record.domainTypeName,
                // Handle both v1 (priority) and v2 (currentImportanceLevel) structures
                currentImportanceLevel: historyItem.importanceLevel,
                priority: historyItem.importanceLevel || historyItem.priority,
                history: [],
              },
              historyItems: [],
            };
          }
        } else {
          // Old history item without ID - show as separate card
          if (!groupedByExternalRxId['_no_id_']) {
            groupedByExternalRxId['_no_id_'] = { main: null, historyItems: [] };
          }
          groupedByExternalRxId['_no_id_'].historyItems.push({
            ...historyItem,
            taxonomyName: record.taxonomyName,
            domainTypeId: record.domainTypeId,
            domainTypeName: record.domainTypeName,
            // Handle both v1 (priority) and v2 (currentImportanceLevel) structures
            currentImportanceLevel: historyItem.importanceLevel,
            priority: historyItem.importanceLevel || historyItem.priority,
          });
        }
      });

      // Convert groups to flat array
      const result = [];
      Object.values(groupedByExternalRxId).forEach(group => {
        if (group.main) {
          result.push({ ...group.main, history: group.historyItems });
        }
        group.historyItems?.forEach(item => {
          if (!group.main) result.push(item);
        });
      });

      return result;
    }

    // For normal records, return as-is
    return [record];
  });
};

/**
 * Sort medications with external RX pushed to the bottom, preserving original order within each group
 * @param {Array} medications - Array of medication records
 * @returns {Array} - Sorted array
 */
export const sortMedicationsWithExternalLast = medications => {
  const normal = [];
  const external = [];

  medications.forEach(med => {
    const isExternal =
      med.currentImportanceLevel === 'ACTIVE_EXTERNAL_RX' ||
      med.currentImportanceLevel === 'INACTIVE_EXTERNAL_RX' ||
      med.priority?.name === 'ACTIVE_EXTERNAL_RX' ||
      med.priority?.name === 'INACTIVE_EXTERNAL_RX';

    if (isExternal) {
      external.push(med);
    } else {
      normal.push(med);
    }
  });

  return [...normal, ...external];
};
